import { observable, action, computed } from 'mobx';

import { cdnFile } from '@/utils/cdnFile';
import API from '@/services';
import sessionStore from './session';
import performanceStore from './performance';
import sessionIDListStore from './sessionIDList';
import coreStore from './core';
import {
  sendCBHeartbeat,
  getRecoveryHistory,
  bridgeCallFunc,
  proxyGetSessionInfoFromBubble,
} from '@/utils/instantLoaded/tools';
import { formatFromInUrl, getQueryForm, addData } from '@/utils/instantLoaded/toolsWithoutDeps';
import { AGENT_AVATAR, BOT_AVATAR, LANG } from '@/utils/locale';
import { handleResponse } from '@/utils';
import { dispatchTrack } from '@/utils/tracking';
import { isFood, getUserSessionRequestUrl } from '@/utils/saas';

import {
  TRACKING_OPERATION,
  SessionIDType,
  FAKE_SESSION_ID,
  ACTUAL_ENTRY_POINT,
  PREMIUM_USER_ENTRY_POINT,
  ENTRY_BUTTON_TYPE,
  CHATBOT_TYPE,
  FROM,
  SourceInUrl,
  ClientType,
  LiveAgentControl,
  PAGE_HEAD_TITLE,
  LIVE_AGENT_DATA_KEY,
} from '@/utils/constants';
import {
  getSearchUrl,
  getFromInUrl,
  isBank,
  UseLiveChatSDKApp,
  isSupportChatbot,
  getConfig,
  isSupportComposedInitSession,
} from '@/config/core';
import { initLiveChatSDK, isBankNonLogin } from '@/utils/liveChatToken';
import { addDefaultGreeting, addShopeeFoodChatWithAgent } from '@/utils/sessionUtils';
import userStore from './user';
import { capitalizeFirstLetter } from '@/utils/tracking/tracker/ChatbotBITrack';
import reporter from '@/utils/report';
import { getTrans } from '@/utils/instantLoaded/getTrans';
import { isBatchShowSkeleton, renderFirstScreenSkeleton } from '@/utils/firstScreenSkeletonControl';
import { handleFirstScreenSkeletonWhenInitSessionFail } from '@/utils/firstScreenSkeleton';
import { isLiveAgentScUIOptimisation } from '@/utils/experiment/getShortCutUIUXExperiment';

// source埋点
const sendSourceTrack = (sessionId, searchUrl, isRRRepeatedSession = false, isOrderRepeatedSession = false) => {
  const from = getQueryForm('from', searchUrl);
  dispatchTrack({
    operation: 'action_session_generated',
    data: {
      entry_point: capitalizeFirstLetter(from) || 'default',
      chat_session_id: sessionId,
      match_repeated_session_check_rr: Number(isRRRepeatedSession),
      match_repeated_session_order: Number(isOrderRepeatedSession),
    },
  });
};

// segment埋点
const sendSegmentTrack = (data) => {
  const { sessionId, isLiveAgentShortcutDisplay, segmentId, searchUrl } = data;
  const from = getQueryForm('from', searchUrl);
  dispatchTrack({
    operation: 'action_hit_segment',
    data: {
      chat_session_id: sessionId,
      is_live_agent_shortcut_display: isLiveAgentShortcutDisplay,
      segment_id: segmentId,
      entry_point: capitalizeFirstLetter(from) || 'default',
    },
  });
};

// 上报init session成功完成的计数
const reportChatbotFirstInitComplete = () => {
  reporter.apiBatchReport({ type: 'count', value: { label: 'chatbot_first_init_complete' } });
};
// 上报不走chatbot功能的计数，用于 html_pv_total - non_chatbot_init_total标志真实chatbot pv
const reportNonChatbotInit = () => {
  reporter.apiBatchReport({ type: 'count', value: { label: 'non_chatbot_init_total' } });
};

const formatShortcut = (intent) => {
  return {
    actual_message: intent.shortcut_text,
    query_callback: intent.query_callback,
    shortcut_id: intent.intent_id,
    shortcut_message: intent.shortcut_text,
    intent_type: intent.intent_type,
    intent_source: intent.intent_source,
    intent_id: intent.intent_id,
    is_live_agent: intent.is_live_agent,
    shortcut_type: intent.shortcut_type,
    need_hide_when_live_agent_shortcut_show: intent.need_hide_when_live_agent_shortcut_show,
    type: 1,
  };
};

const getDefaultNickname = () => getTrans(getConfig().botTitleContent || PAGE_HEAD_TITLE);

let resolve;
export let initSessionApiFinish;

const storageBotAvatar = localStorage.getItem('botAvatar');

const showInputAreaDeadLine = 3000;

// 初始化(欢迎语、HotQuestions、Shortcuts等)
export class FirstInitStore {
  @observable initStore = {
    // store data
    isFirstInit: true,
    dateTime: new Date().getTime(),
    searchUrl: getSearchUrl(),
    sessionId: '',
    sessionIdKey: '',
    sessionToken: '',
    liveAgentChatVersion: 1,
    appPolyfillVersion: 0,
    shortcutList: [],
    shortcutType: 0,
    botAvatar: storageBotAvatar || BOT_AVATAR,
    botNickname: '',
    grayFeatures: undefined,
    abTestSignature: '',
    agentAvatar: AGENT_AVATAR,
    from: getFromInUrl(),
    queueAvatar: BOT_AVATAR,
    chattingAvatar: AGENT_AVATAR,

    shouldBottomBarHidden: false,
    isMainRendered: false,
    isPremiumUser: false,
    hideLiveAgentSegmentIds: [],
    chatbotType: CHATBOT_TYPE.NORMAL_CHATBOT,
    appType: 0,
    botType: 0,
    initSessionChatConfig: {},
    kbBotId: undefined,
    kbBotName: undefined,
    showChatbotCsat: false,
    csatRnBundleVersion: '',
    isDirectToLiveChatForNewMarket: true,
    liveAgentShortcut: null,
    liveAgentShortcutIndex: 0,
    hideLiveAgentShortcuts: false,
    liveAgentSetting: { allow_live_agent: true, no_live_agent_tips: '' },
    showSkeleton: false,
    submitWebformSwitch: false,
  };

  @observable inputStore = {
    userInput: '',
    shouldReportTyping: true,
    shouldShowSuggestion: {
      showStatus: false,
      dataSource: [],
      data: [],
    },
    inputRef: { current: null },
    compositing: false,
    isInputFocus: false,
  };

  @observable historyStore = {
    initMessageId: '',
    beforeMessageId: '',
  };

  @observable initPromise = new Promise((rs) => {
    resolve = rs;
  });

  @observable initApiPromise = new Promise((rs) => {
    initSessionApiFinish = rs;
  });

  @observable userSwitchShowLiveAgent = false;

  // 目前入口 from 都是字符串 entrypoint ID，但是代码中还有几处依赖数字 entrypoint ID， 如
  // init session 接口, inhouse 进线
  @observable entrypointIdInNumber;

  // 以下是batch show first screen shortcut展示控制条件
  // 是否可以展示shortcut
  @observable canShowShortcut = false;

  // 是否展示shortcut骨架图
  @observable showShortcutsSkeleton = false;

  // 网络延时或接口失败时，到超时时间显示 Input
  @observable canShowInputArea = false;
  // shortcut 控制条件 end

  @observable showFirstLoadLoading = false;

  constructor() {
    setTimeout(() => {
      this.UPDATE_CAN_SHOW_INPUT_AREA(true);
    }, showInputAreaDeadLine);
  }

  @action
  UPDATE_INITSTORE(data) {
    this.initStore = {
      ...this.initStore,
      ...data,
    };
  }

  @action
  UPDATE_MAINSTORE() {
    const mainStore = coreStore.getStore('initStore');

    mainStore?.COLLECT_FIRSTINIT_STORE();
  }

  @action
  UPDATE_INPUTSTORE(data) {
    this.inputStore = {
      ...this.inputStore,
      ...data,
    };
  }

  @action
  UPDATE_HISTORYSTORE(data) {
    this.historyStore = {
      ...this.historyStore,
      ...data,
    };
  }

  // initStore 部分
  @action
  UPDATE_LOADING_TO_GET_I18N(loadingToGetI18N) {
    this.UPDATE_INITSTORE({ loadingToGetI18N });
  }

  getInitSessionParams() {
    const orderid = getQueryForm('orderid', this.initStore.searchUrl);
    const returnid = getQueryForm('returnid', this.initStore.searchUrl);
    const deliveryId = getQueryForm('deliveryId', this.initStore.searchUrl);
    const currentDeliveryId = getQueryForm('currentDeliveryId', this.initStore.searchUrl);
    const driverDeliveryId = getQueryForm('driverDeliveryOrderId', this.initStore.searchUrl);
    const serviceType = getQueryForm('serviceType', this.initStore.searchUrl);

    const entryPointInUrl = formatFromInUrl(this.initStore.from);
    this.updateEntryPointId(ACTUAL_ENTRY_POINT[entryPointInUrl] || entryPointInUrl);
    // 对于 mePagePremium 和 helpCentrePremium 做映射
    // 传给后端的值是 mePage 和 helpCentre 的值

    const params = {
      entry_point: this.entrypointIdInNumber,
      order_id: orderid,
      return_id: returnid,
      button_type: PREMIUM_USER_ENTRY_POINT.includes(entryPointInUrl)
        ? ENTRY_BUTTON_TYPE.PREMIUM_BUTTON
        : ENTRY_BUTTON_TYPE.NORMAL_BUTTON,
      // 标识后端采用新版本还是旧版本，发布用
      api_version: 2,
      language: LANG,
      client_type: ClientType.App,
      driver_delivery_id: driverDeliveryId,
      delivery_id: deliveryId,
      current_step_delivery_id: currentDeliveryId,
      service_type: serviceType,
    };

    return params;
  }

  @action
  async COMPOSED_FETCH_DATA(skipRecoverChannel = false) {
    const entryPointInUrl = formatFromInUrl(this.initStore.from);

    let goOldLogic = false;
    if (isSupportComposedInitSession()) {
      const storedAgentData = window.localStorage.getItem(LIVE_AGENT_DATA_KEY);

      let agentData;
      try {
        agentData = JSON.parse(storedAgentData);
      } catch (err) {
        console.log('err: ', err);
      }
      let getBubbleSessionInfoPromise = null;
      if (agentData?.agentStoreData?.sessionKey) {
        getBubbleSessionInfoPromise = proxyGetSessionInfoFromBubble({ sessionKey: agentData.agentStoreData.sessionKey });
      }

      const userSessionRequestUrl = getUserSessionRequestUrl();

      const composedInitSessionParams = {
        entry_point: ACTUAL_ENTRY_POINT[entryPointInUrl] || entryPointInUrl,
        button_type: PREMIUM_USER_ENTRY_POINT.includes(entryPointInUrl)
          ? ENTRY_BUTTON_TYPE.PREMIUM_BUTTON
          : ENTRY_BUTTON_TYPE.NORMAL_BUTTON,
        // 标识后端采用新版本还是旧版本，发布用
        api_version: 2,
        language: LANG,
        client_type: ClientType.App,
        from: 'h5',
        channel: 1, // ChatbotChannel
        skip_recover_channel: skipRecoverChannel,
        user_session_request_url: userSessionRequestUrl,
      };
      const preComposedRes = await window.preRequest?.preGetComposedInitSession;
      const [composedRes, bubbleSessionInfo] = await Promise.all([
        preComposedRes || API.getComposedInitSession(composedInitSessionParams).catch(() => null),
        getBubbleSessionInfoPromise,
      ]);

      if (composedRes && !bubbleSessionInfo) {
        if (!preComposedRes) {
          const {
            user_info,
            user_switch,
            experiment_result,
            need_recover_channel,
            live_chat_user_session,
            virtual_call_status_check,
            ...firstInitSession
          } = composedRes;

          window.preRequest.preGetComposedInitSession = skipRecoverChannel ? null : composedRes;
          window.preRequest.preGetUserInfo = user_info?.is_login === false ? {} : user_info;
          window.preRequest.preGetUserSwitch = user_switch;
          window.preRequest.preGetExperiment = experiment_result;
          if (live_chat_user_session) {
            const {
              is_exist,
              session_key,
              status,
              live_agent_type,
              common_ext_info = {},
              sf_ext_info = {},
            } = live_chat_user_session || {};

            window.preRequest.preGetLiveChatUserSession = {
              status,
              isExist: is_exist === 1, // is_exist可能为0表示不存在，status为ended时，is_exist可能为0
              sessionKey: session_key,
              channelType: live_agent_type,
              userSessionExtensionInfo: {
                ...common_ext_info,
                ...sf_ext_info,
              },
            };
          }
          window.preRequest.preGetVirtualCallStatusCheck = virtual_call_status_check;

          const loadingPageShowGroup = experiment_result?.result?.loading_page_batch_show?.group_name;
          if (loadingPageShowGroup && need_recover_channel === false) {
            const batchShow
              = ['batch_show', 'hide_typing_box'].includes(loadingPageShowGroup)
              && this.initStore.from !== FROM.ShopeeFoodHC;
            const params = this.getInitSessionParams();

            if (batchShow) {
              const firstRes = firstInitSession?.session_id ? firstInitSession : null;
              const initSession = firstRes
                ? Promise.resolve(firstRes).then((res) => {
                  return API.getInitSession(
                    { ...params, new_session_id: res.session_id },
                    { isErrorWhiteList: true },
                  );
                })
                : null;
              window.preRequest.preGetFirstInitSession = firstRes;
              window.preRequest.preGetInitSession = initSession;
            } else {
              window.preRequest.preGetInitSession = API.getInitSession(params);
            }
          }
        }

        const { need_recover_channel } = composedRes;

        if (need_recover_channel || !isSupportChatbot()) {
          reportNonChatbotInit();
        }

        if (need_recover_channel === false || skipRecoverChannel) {
          if (isSupportChatbot()) {
            await this.FETCH_DATA();
          } else {
            await this.FETCH_STATIC_RESOURCES();
          }
        }
      } else {
        goOldLogic = true;
      }
    } else {
      goOldLogic = true;
    }

    if (goOldLogic) {
      window.preRequest.preGetComposedInitSession = null;
      // old logic
      const recoveryHistory = await getRecoveryHistory();
      if (recoveryHistory || !isSupportChatbot()) {
        reportNonChatbotInit();
      }

      if (!recoveryHistory) {
        if (isSupportChatbot()) {
          await this.FETCH_DATA(isBank());
        } else {
          await this.FETCH_STATIC_RESOURCES();
        }
      }
    }
  }

  @action
  async INIT_CHATBOT() {
    if (this.initStore.isFirstInit) {
      if (UseLiveChatSDKApp.is()) {
        const initResult = await initLiveChatSDK({ clearLastChatAccessToken: true });
        if (initResult instanceof Error) {
          console.error('firstStore initLiveChatSDK, initResult=', initResult);
          this.INIT_DATETIME(this.initStore.dateTime);
          this.UPDATE_SESSIONID(FAKE_SESSION_ID);
          // 设置默认 nickname
          this.UPDATE_NICKNAME();
          // 同步store至main组件
          this.UPDATE_MAINSTORE();

          reportNonChatbotInit();

          resolve(new Error('get access token failed'));
          return;
        }
        if (!isSupportChatbot()) {
          initSessionApiFinish();
        }
      }

      performanceStore.SET_FIRST_PAINT(window.performance.now());

      let shoudCallComposedFetchData = true;
      try {
        const { isLandingFromBankLoginPage } = await import('@/utils/bankLoginHelpers');
        console.log('INIT_CHATBOT isBank() && !isBankNonLogin && isLandingFromBankLoginPage()', isBank(), !isBankNonLogin, isLandingFromBankLoginPage());
        if (isBank() && !isBankNonLogin && isLandingFromBankLoginPage()) {
          // bank non-login -> login retain context message
          if (isBank() && !isBankNonLogin && isLandingFromBankLoginPage()) {

            const { retainSessionContext } = await import('@/utils/bankLoginHelpers');
            if (await retainSessionContext()) {
              console.log('INIT_CHATBOT retainSessionContext sucess');
              shoudCallComposedFetchData = false;
              // 因为FE revocer the  conetxt data，所以直接设置promise fullfilled
              resolve();
              initSessionApiFinish();
              return;
            }
          }
        }
      } catch (e) {
        console.error('firstStore INIT_CHATBOT, shoudCallComposedFetchData, e=', e);
      }

      if (shoudCallComposedFetchData) {
        await this.COMPOSED_FETCH_DATA();
      }

      // https://jira.shopee.io/browse/SPCB-21522
      bridgeCallFunc(() => {
        if (window.isShopeeApp()) {
          window.bridgeCallHandler('getAppInfo', null, (res) => {
            reporter.apiBatchReport({
              type: 'count',
              value: {
                label: 'shopee_app_language_statistic',
                extend_1: res.appLanguage,
              },
            });
          });
        }
      });
      // 如果恢复的情况下不会执行FETCH_DATA和FETCH_STATIC_RESOURCES，也就不会resolve了，这种情况下还是要让firstinit resolve
      resolve();
    }
  }

  @action
  updateEntryPointId(id) {
    this.entrypointIdInNumber = id;
  }

  @action
  async FETCH_DATA(hideFirstScreenSkeleton = false) {
    const source = getQueryForm('__source__', this.initStore.searchUrl);
    const isFromPN = source === SourceInUrl.PUSH_NOTIFICATION;

    const preGetUserSwitch = await window.preRequest?.preGetUserSwitch;
    (preGetUserSwitch ? Promise.resolve(preGetUserSwitch) : API.getUserSwitch())
      .then((res) => {
        this.UPDATE_USER_SWITCH_SHOW_LIVE_AGENT(res?.live_agent_switch);
      })
      .catch();

    // 非PN则初始化时间
    // PN的初始化放在initStore中进行
    !isFromPN && this.INIT_DATETIME(this.initStore.dateTime);

    const params = this.getInitSessionParams();

    await userStore.FETCH_ABTEST().catch(() => { });

    !hideFirstScreenSkeleton && this.initStore.isFirstInit && renderFirstScreenSkeleton();

    let initRes = {};
    if (isBatchShowSkeleton()) {
      const preFirstRes = await window.preRequest?.preGetFirstInitSession;
      // first init session start
      const firstRes = preFirstRes || (await API.getFirstInitSession(params).catch(() => { }));
      // 重置，有结果也只使用一次
      window.preRequest && (window.preRequest.preGetFirstInitSession = null);

      initSessionApiFinish();

      if (firstRes) {
        const { init_message_id, user_entry_type } = firstRes;

        this.UPDATE_HISTORYSTORE({
          initMessageId: init_message_id,
          beforeMessageId: init_message_id,
        });

        // 直接连线 premium chat
        if (user_entry_type === CHATBOT_TYPE.PREMIUM_LIVE_CHAT) {
          const avatar = cdnFile(firstRes.avatar);
          const liveChatAvatar = cdnFile(firstRes.live_chat_avatar);

          this.SET_INIT_DATA({
            chatbot_type: user_entry_type,
            avatar,
            liveChatAvatar,
          });

          this.UPDATE_MAINSTORE(); // 同步store至main组件
        } else {
          // PN在initStore中handleResponse
          if (!isFromPN) {
            handleResponse({ res: firstRes, isFirstInitSession: this.initStore.isFirstInit });
          }
        }
        this.ANNOUNCEMENT_DATA_CONFIG(firstRes);
        initRes = { ...firstRes };
      }

      // 性能标识api_request
      performanceStore.SET_API_REQUEST(window.performance.now());

      if (!firstRes) {
        handleFirstScreenSkeletonWhenInitSessionFail();
        // init session 接口失败，伪造一个 fake sessionId
        this.UPDATE_SESSIONID(FAKE_SESSION_ID);
        sendSourceTrack(FAKE_SESSION_ID, this.initStore.searchUrl, false, false);

        await this.FETCH_RESOURCES();

        this.UPDATE_NICKNAME(); // 设置默认 nickname
        this.UPDATE_MAINSTORE(); // 同步store至main组件

        // 性能标识first_paint
        performanceStore.SET_FIRST_INTERACTIVE(window.performance.now());
        reportChatbotFirstInitComplete();
        // init_session失败后，发性能埋点
        performanceStore.SEND_TRACK();
        resolve();
        return;
      }

      // first init session end

      initRes.session_id && this.UPDATE_SESSIONID(initRes.session_id);
      initRes.session_token && this.UPDATE_SESSIONTOKEN(initRes.session_token);

      const preInitRes = await window.preRequest?.preGetInitSession;
      // second init session start
      const res
        = preInitRes
        || (await API.getInitSession({ ...params, new_session_id: firstRes.session_id }, { isErrorWhiteList: true }).catch(
          () => { },
        ));
      // 重置，有结果也只使用一次
      window.preRequest && (window.preRequest.preGetInitSession = null);

      if (res) {
        const { live_agent_setting } = res;

        // 直接连线 premium chat
        if (this.initStore.chatbotType === CHATBOT_TYPE.PREMIUM_LIVE_CHAT) {
          handleFirstScreenSkeletonWhenInitSessionFail(true);
          this.SET_INIT_DATA({ live_agent_setting });

          this.UPDATE_MAINSTORE(); // 同步store至main组件

          reportChatbotFirstInitComplete();

          resolve();
          // 相关处理放在initStore中
          return;
        }

        // PN在initStore中handleResponse
        if (!isFromPN) {
          handleResponse({ res, isInitSession: this.initStore.isFirstInit });
        }

        initRes = { ...initRes, ...res };
      }

      if (!res) {
        handleFirstScreenSkeletonWhenInitSessionFail(false);
      }
      // second init session end
    } else {
      const preInitRes = await window.preRequest?.preGetInitSession;
      const res = preInitRes || (await API.getInitSession(params).catch(() => { }));
      // 重置，有结果也只使用一次
      window.preRequest && (window.preRequest.preGetInitSession = null);

      initSessionApiFinish();

      this.UPDATE_SHOW_SKELETON(false);

      if (res) {
        const { init_message_id, user_entry_type, live_agent_setting } = res;

        this.UPDATE_HISTORYSTORE({
          initMessageId: init_message_id,
          beforeMessageId: init_message_id,
        });

        // 直接连线 premium chat
        if (user_entry_type === CHATBOT_TYPE.PREMIUM_LIVE_CHAT) {
          const avatar = cdnFile(res.avatar);
          const liveChatAvatar = cdnFile(res.live_chat_avatar);

          this.SET_INIT_DATA({
            chatbot_type: user_entry_type,
            avatar,
            liveChatAvatar,
            live_agent_setting,
          });

          this.UPDATE_MAINSTORE(); // 同步store至main组件

          reportChatbotFirstInitComplete();

          resolve();
          // 相关处理放在initStore中
          return;
        }

        // PN在initStore中handleResponse
        if (!isFromPN) {
          handleResponse({ res, isInitSession: this.initStore.isFirstInit });
        }
        this.ANNOUNCEMENT_DATA_CONFIG(res);
        initRes = { ...res };
      }

      // 性能标识api_request
      performanceStore.SET_API_REQUEST(window.performance.now());

      if (!res) {
        // init session 接口失败，伪造一个 fake sessionId
        this.UPDATE_SESSIONID(FAKE_SESSION_ID);
        sendSourceTrack(FAKE_SESSION_ID, this.initStore.searchUrl, false, false);

        await this.FETCH_RESOURCES();
        this.UPDATE_NICKNAME(); // 设置默认 nickname
        this.UPDATE_MAINSTORE(); // 同步store至main组件

        // 性能标识first_paint
        performanceStore.SET_FIRST_INTERACTIVE(window.performance.now());
        reportChatbotFirstInitComplete();
        // init_session失败后，发性能埋点
        performanceStore.SEND_TRACK();
        resolve();
        return;
      }

      initRes.session_id && this.UPDATE_SESSIONID(initRes.session_id);
      initRes.session_token && this.UPDATE_SESSIONTOKEN(initRes.session_token);
    }

    reporter.apiLog(`【init_session】result: ${JSON.stringify(initRes)};`, { level: 'debug' });

    /* 如果是shopeeFood渠道，则默认显示greeting（可配置）和chat with agent逻辑 */
    if (this.initStore.isFirstInit && this.initStore.from === FROM.ShopeeFoodHC) {
      addDefaultGreeting(Array.isArray(initRes.greetings) && initRes.greetings[0]);
      addShopeeFoodChatWithAgent();
    }

    // 第一次请求init_session时，发送来源埋点
    this.initStore.isFirstInit
      && sendSourceTrack(
        initRes.session_id,
        this.initStore.searchUrl,
        initRes.match_repeated_session_check_rr,
        initRes.match_repeated_session_order,
      );

    this.initStore.isFirstInit
      && sendSegmentTrack({
        sessionId: initRes.session_id,
        isLiveAgentShortcutDisplay: initRes.live_agent_shortcut
          ? initRes.hide_liveagent_shortcuts === LiveAgentControl.hide
            ? 0
            : 1
          : 0,
        segmentId: initRes.hide_live_agent_segment_ids?.join(','),
        searchUrl: this.initStore.searchUrl,
      });

    // avatar转base64
    // const avatar = await urlToBase64(cdnFile(res.avatar)).catch(() => { });
    const avatar = cdnFile(initRes.avatar);
    const liveChatAvatar = cdnFile(initRes.live_chat_avatar);

    if (avatar) {
      localStorage.setItem('botAvatar', avatar);
    }

    this.SET_INIT_DATA({
      ...initRes,
      abTestSignature: initRes.experiment_platform_signature,
      avatar,
      liveChatAvatar,
      chatbot_type: initRes?.user_entry_type,
    });

    reportChatbotFirstInitComplete();

    // 在租户bank，chatbot成功初始化，也需要特殊支持拉取live chat avatar资源
    if (isBank()) {
      const res = await API.getBankStaticResource({ entry_point: formatFromInUrl(getFromInUrl()) }).catch(() => { });
      if (res) {
        const { live_chat_chatting_avatar, floating_bubble_queuing_avatar, floating_bubble_chatting_avatar } = res;

        this.UPDATE_INITSTORE({
          agentAvatar: live_chat_chatting_avatar,
          queueAvatar: floating_bubble_queuing_avatar,
          chattingAvatar: floating_bubble_chatting_avatar,
        });
      }
    }

    this.UPDATE_MAINSTORE(); // 同步store至main组件

    resolve(isFromPN ? initRes : undefined);

    // 初始进入ChatBot，有session_id后，发起心跳
    sendCBHeartbeat(true);
    // second init session end
  }

  @action
  async FETCH_STATIC_RESOURCES() {
    if (!UseLiveChatSDKApp.supportChatbot()) {
      this.INIT_DATETIME(this.initStore.dateTime);
    }

    this.UPDATE_SESSIONID(FAKE_SESSION_ID);

    // initSessionApiFinish();
    await this.FETCH_RESOURCES();

    // 设置默认 nickname
    this.UPDATE_NICKNAME();
    // 同步store至main组件
    this.UPDATE_MAINSTORE();
    resolve();
  }

  async FETCH_RESOURCES() {
    if (isBank()) {
      const res = await API.getBankStaticResource({ entry_point: formatFromInUrl(getFromInUrl()) }).catch(() => { });
      if (res) {
        const {
          live_chat_before_chatting_avatar,
          live_chat_chatting_avatar,
          floating_bubble_queuing_avatar,
          floating_bubble_chatting_avatar,
        } = res;

        this.SET_INIT_DATA({
          avatar: live_chat_before_chatting_avatar,
          liveChatAvatar: live_chat_chatting_avatar,
          queueAvatar: floating_bubble_queuing_avatar,
          chattingAvatar: floating_bubble_chatting_avatar,
        });
      }
    } else if (isFood()) {
      // food 全部使用默认的不再请求接口
      return;
    } else {
      const res = await API.getStaticResources().catch(() => { });
      if (res) {
        const { avatar, live_chat_avatar } = res;

        this.SET_INIT_DATA({
          avatar: cdnFile(avatar),
          liveChatAvatar: cdnFile(live_chat_avatar),
        });
      }
    }
  }

  @action
  UPDATE_NICKNAME() {
    this.UPDATE_INITSTORE({ botNickname: getDefaultNickname() });
  }

  @action
  INIT_DATETIME(dateTime) {
    const dialogueList = {
      from: 'system',
      type: 'DateTime',
      data: { time: dateTime },
    };
    sessionStore.ADD_INIT_LIST(dialogueList);
    // this.ADD_INIT_LIST(dialogueList);
  }

  @action
  UPDATE_SESSIONID(id = '') {
    this.UPDATE_INITSTORE({
      sessionId: id,
      sessionIdKey: id,
    });

    reporter.apiLog(`【UPDATE_SESSIONID】sessionId: ${id}; userId=${userStore.userId}`, { level: 'debug' });

    // 更新sessionId的同时，将其放入sessionIDList
    sessionIDListStore.UPDATE_SESSION_ID_LIST({
      type: SessionIDType.CHAT_BOT,
      id,
    });
  }

  @action
  UPDATE_SESSIONTOKEN(token = '') {
    this.UPDATE_INITSTORE({ sessionToken: token });
  }

  @action
  SET_INIT_DATA(data) {
    const {
      session_id,
      session_token,
      live_agent_chat_version = 1,
      shortcuts,
      shortcut_intents,
      avatar,
      liveChatAvatar,
      dateTime,
      gray_features,
      app_polyfill_version,
      searchUrl,
      abTestSignature,
      shortcut_type = 0,
      submit_webform_switch = false,
      chatbot_type = 1,
      hide_live_agent_segment_ids = [],
      app_type = 0,
      kb_bot_id,
      kb_bot_name,
      csat_switch,
      csat_rn_version,
      queueAvatar,
      chattingAvatar,
      is_direct_to_live_chat_for_new_market,
      live_agent_shortcut,
      live_agent_insert_idx,
      hide_liveagent_shortcuts,
      live_agent_setting,
      nickname,
      textbox_default_suggestion,
      request_id,
      bot_type,
      chat_config,
    } = data;

    // 抹平shortcut和shortcut_intents返回数据的差异
    let shortcutList = this.initStore.shortcutList;
    const liveAgentShortcut = live_agent_shortcut && formatShortcut(live_agent_shortcut);
    if (shortcuts && shortcut_type === 0) {
      shortcutList = shortcuts;
    } else if (shortcut_intents && shortcut_type === 1) {
      shortcutList = shortcut_intents.map(formatShortcut);
    }

    this.UPDATE_INITSTORE({
      sessionId: session_id || this.initStore.sessionId,
      sessionIdKey: session_id || this.initStore.sessionIdKey,
      sessionToken: session_token || this.initStore.sessionToken,
      liveAgentChatVersion: live_agent_chat_version,
      appPolyfillVersion: app_polyfill_version,
      dateTime: dateTime || this.initStore.dateTime,
      shortcutList: shortcutList,
      shortcutType: shortcut_type,
      botAvatar: avatar || this.initStore.botAvatar,
      grayFeatures: gray_features || this.initStore.grayFeatures,
      searchUrl: searchUrl || this.initStore.searchUrl,
      abTestSignature: abTestSignature || this.initStore.abTestSignature,
      agentAvatar: liveChatAvatar || this.initStore.agentAvatar,
      queueAvatar: queueAvatar || this.initStore.queueAvatar,
      chattingAvatar: chattingAvatar || this.initStore.chattingAvatar,
      chatbotType: chatbot_type,
      appType: app_type,
      hideLiveAgentSegmentIds: hide_live_agent_segment_ids || this.initStore.hideLiveAgentSegmentIds,
      isPremiumUser: chatbot_type === CHATBOT_TYPE.PREMIUM_CHATBOT,
      kbBotId: kb_bot_id,
      kbBotName: kb_bot_name,
      showChatbotCsat: csat_switch || this.initStore.showChatbotCsat,
      csatRnBundleVersion: csat_rn_version || this.initStore.rnBundleVersion,
      isDirectToLiveChatForNewMarket: is_direct_to_live_chat_for_new_market,
      liveAgentShortcut: liveAgentShortcut,
      liveAgentShortcutIndex: isLiveAgentScUIOptimisation() ? 0 : live_agent_insert_idx,
      hideLiveAgentShortcuts: hide_liveagent_shortcuts === LiveAgentControl.hide ? true : false,
      liveAgentSetting: live_agent_setting ?? this.initStore.liveAgentSetting,
      botNickname: nickname || getDefaultNickname(),
      inputBoxSuggestionIntent: { intent: textbox_default_suggestion, requestId: request_id },
      botType: bot_type,
      submitWebformSwitch: submit_webform_switch,
    });

    // 首次请求，添加greeting消息 & hotQuestions
    if (this.initStore.isFirstInit) {
      performanceStore.SET_FIRST_INTERACTIVE(window.performance.now());
      // init_session成功后，发性能埋点
      performanceStore.SEND_TRACK();

      // 把isFirstInit置为false
      this.UPDATE_NOTFIRST();
    }

    // 填充 chat_config
    this.UPDATE_INIT_SESSION_CHAT_CONFIG(chat_config);
  }

  @action
  UPDATE_NOTFIRST() {
    // 首次请求flag置为false
    this.UPDATE_INITSTORE({ isFirstInit: false });
  }

  @action
  UPDATE_IS_PREMIUM_USER(isPremiumUser) {
    this.UPDATE_INITSTORE({ isPremiumUser });
  }

  // inputStore 部分
  /**
   * @param {string} value
   */
  @action
  HANDLE_INPUT_CHANGE(event) {
    if (this.inputStore.shouldReportTyping) {
      dispatchTrack({
        operation: TRACKING_OPERATION.ACTION_START_TYPING,
        data: { chat_session_id: this.initStore.sessionId },
      });
      this.UPDATE_shouldReportTyping(false);
    }
    this.UPDATE_USER_INPUT(event.target.value);
  }

  @action
  UPDATE_USER_INPUT(value) {
    this.UPDATE_INPUTSTORE({ userInput: value });
  }

  @action
  UPDATE_shouldReportTyping(shouldReportTyping) {
    this.UPDATE_INPUTSTORE({ shouldReportTyping });
  }

  @action
  UPDATE_INPUT_DOM_REF(ref) {
    this.UPDATE_INPUTSTORE({ inputRef: ref });
  }

  @action
  UPDATE_INPUT_FOCUS(flag = false) {
    this.UPDATE_INPUTSTORE({ isInputFocus: flag });
  }

  @action
  UPDATE_headerExtraTop(num = 0) {
    this.UPDATE_INPUTSTORE({ headerExtraTop: num });
  }

  @action
  UPDATE_HIDE_LIVE_AGENT_SHORTCUTS(control) {
    switch (control) {
      case LiveAgentControl.hide:
        this.hideLiveAgentShortcuts = true;
        break;
      case LiveAgentControl.show:
        this.hideLiveAgentShortcuts = false;
        break;
      case LiveAgentControl.remain:
      default:
        break;
    }
  }

  @action
  UPDATE_USER_SWITCH_SHOW_LIVE_AGENT(bool = false) {
    this.userSwitchShowLiveAgent = bool;
  }

  @action
  UPDATE_SHOW_SKELETON(showSkeleton) {
    this.initStore.showSkeleton = showSkeleton;
  }

  @action
  UPDATE_SEARCH_URL(searchUrl = '') {
    this.initStore.searchUrl = searchUrl;
    this.initStore.from = getQueryForm('from', searchUrl);
  }

  @computed
  get dataList() {
    let { sessionIDList } = sessionIDListStore;
    sessionIDList = sessionIDList.slice();

    const { sessionData, initList } = sessionStore;

    let dataList = [...initList];

    sessionIDList.forEach((idItem) => {
      const { type, id } = idItem;
      switch (type) {
        case SessionIDType.CHAT_BOT:
          dataList = addData(dataList, sessionData, id);
          break;
        default:
      }
    });

    return dataList;
  }

  @computed
  get dataListLength() {
    return this.dataList.length;
  }

  @action
  UPDATE_SEARCH_URL_AND_FROM(searchUrl = '') {
    this.initStore.searchUrl = searchUrl;
    this.initStore.from = getQueryForm('from', searchUrl);
  }

  @action
  UPDATE_IS_SHOW_SHORTCUT_SKELETON(bool) {
    this.showShortcutsSkeleton = bool;
  }

  @action
  UPDATE_CAN_SHOW_INPUT_AREA(bool) {
    this.canShowInputArea = bool;
  }

  @action
  UPDATE_CAN_SHOW_SHORTCUT(bool) {
    this.canShowShortcut = bool;
  }

  @action
  async ANNOUNCEMENT_DATA_CONFIG(data) {
    const { default: modalStore } = await import('./modal');
    const { REPLY_TYPE } = await import('@/utils/responseHandler');
    const announcements = data?.replies?.find((v) => v?.reply_type === REPLY_TYPE.ANNOUNCEMENT);
    modalStore.POP_UP_ANNOUNCEMENT_CONFIG(announcements);
    const { default: initStore } = await import('./init');
    initStore.UPDATE_PINNED_ANNOUNCEMENT_DATA(announcements?.message_extra?.announcements);
    initStore.UPDATE_INIT_ANNOUNCEMENT_DATA(announcements?.message_extra?.announcements);
  }

  @action
  UPDATE_FIRST_LOAD_LOADING(bool) {
    this.showFirstLoadLoading = bool;
  }

  @action
  async UPDATE_INIT_SESSION_CHAT_CONFIG(config) {
    const { mktBridgeCallHandler } = await import('@/utils/instantLoaded/toolsWithoutDeps').catch(() => { });
    const checkVersion = await mktBridgeCallHandler('checkVersion').catch(() => { });
    const { appver } = checkVersion;
    const { getMobileSystem } = await import('@/utils/tools').catch(() => { });
    const newConfig = config || {};
    // 判断 是否 可以使用 internet call
    newConfig.canUseInternetCall = false;
    if (getMobileSystem() === 'ios') {
      newConfig.canUseInternetCall = appver >= newConfig?.virtual_call_ios_version;
    } else if (getMobileSystem() === 'android') {
      newConfig.canUseInternetCall = appver >= newConfig?.virtual_call_android_version;
    }
    const mainStore = coreStore.getStore('initStore');
    mainStore?.UPDATE_INIT_SESSION_CHAT_CONFIG({ ...newConfig });
    // this.UPDATE_INITSTORE({ initSessionChatConfig: { ...newConfig } });
  }
}

const firstInitStore = new FirstInitStore();

export default firstInitStore;
